// src/components/Dashboard/Dashboard.js
import React, { useEffect, useRef, useState } from "react";
import Guage from "./Guage";
import { fetchDevices, fetchTariffDataByBuildingId, fetchUsernameAndEmail } from "../../services/api";
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import Stats from "./Stats";
import colors from "../../theme";
import CardVerifiedSnackbar from "../../components/CardVerifiedSnackbar/CardVerifiedSnackbar";
import Popup from "./IOS-popup";
import { useDispatch, useSelector } from "react-redux";
import { incrementVisit } from "../../store/dashboardVisits";

export default function Dashboard({setDeviceId}) {
  const [chargingPoints, setChargingPoints] = useState([]);
  const [selectedChargingPoint, setSelectedChargingPoint] = useState("");
  const [selectedBuilding, setSelectedBuilding] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const dispatch = useDispatch();
  const visitCount = useSelector((state) => state.visit.visitCount);
  const visitCountOnMount = useRef(visitCount);
  const [isIosBrowser, setIsIosBrowser] = useState(false);
  const [switchState, setSwitchState] = useState(false);

  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    const isInStandaloneMode = window.navigator.standalone;
    const isNativeApp = userAgent.includes("rsp/v");
    setIsIosBrowser(
      isIosDevice &&
        !isInStandaloneMode &&
        !isNativeApp &&
        (visitCountOnMount.current === 0 || visitCountOnMount.current % 5 === 0)
    );
  }, []);

  useEffect(() => {
    dispatch(incrementVisit());
  }, [dispatch]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const devices = await fetchDevices();
        setChargingPoints(devices);

        if (devices.length > 0) {
          const firstDevice = devices[0];
          setSelectedChargingPoint(firstDevice.chargePointId);
          setDeviceId(firstDevice.chargePointId);
          setSelectedBuilding(firstDevice.buildingName);
          await checkTariffAndShowSnackbar(firstDevice.buildingId);
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };

    const fetchDevicesData = async () => {
      setIsLoading(true);
      try {
        const devices = await fetchDevices();
        setChargingPoints(devices);
        if (devices.length > 0) {
          setSelectedChargingPoint(devices[0].chargePointId);
          setSelectedBuilding(devices[0].buildingName);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserInfo();
    fetchDevicesData();
  }, [setDeviceId]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleChargingPointChange = async (event) => {
    const selectedPointId = event.target.value;
    const selectedPoint = chargingPoints.find(
      (point) => point.chargePointId === selectedPointId
    );
    setSelectedChargingPoint(selectedPointId);
    setSelectedBuilding(selectedPoint?.buildingName || "");
    setDeviceId(selectedPointId);

    if (selectedPoint?.buildingId) {
      await checkTariffAndShowSnackbar(selectedPoint.buildingId);
    }
  };

  const checkTariffAndShowSnackbar = async (buildingId) => {
    try {
      const tariffs = await fetchTariffDataByBuildingId(buildingId);
      const latestTariff = tariffs && tariffs.length > 0 ? tariffs[0] : null;
      const peak = parseFloat(latestTariff?.peakRate || 0);
      const offPeak = parseFloat(latestTariff?.offPeakRate || 0);
      const userData = await fetchUsernameAndEmail();

      const userAgent = window.navigator.userAgent.toLowerCase();
      const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
      const isInStandaloneMode = window.navigator.standalone;

      if (userData.paymentMethod !== "verified" && !(peak === 0 && offPeak === 0)) {
        if (isIosDevice) {
          if (isInStandaloneMode) {
            setOpenSnackbar(true);
          }
        } else {
         setOpenSnackbar(true);
        }
      } else {
        setOpenSnackbar(false);
      }
    } catch (error) {
      console.error("Failed to check and show snackbar:", error);
    }
  };

  return (
    <>
      {(isIosBrowser && selectedChargingPoint && (selectedChargingPoint !== "E6E3F5DF8-0" && selectedChargingPoint !== "F55OH9-1")) ? <Popup /> : <></>}
      <Box
        component="main"
        sx={{
          flex: 1,
          py: {
            xs: "0px",
            md: "6px",
          },
          px: {
            xs: "0px",
            md: "4px",
          },
          bgcolor: colors.mainContentBgColor,
        }}
      >
        <CardVerifiedSnackbar
          open={openSnackbar}
          onClose={handleCloseSnackbar}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            justifyContent: "center",
            alignItems: "center",
            px: 4,
          }}
        >
          <FormControl
            fullWidth
            variant="standard"
            sx={{
              width: isSmallScreen ? "FullWidth" : "12rem",
              "& .MuiInput-underline:before": {
                borderBottomColor: colors.secondary,
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: colors.secondary,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: colors.secondary,
              },
            }}
          >
            <InputLabel id="charging-point-select-label">
              Device
            </InputLabel>
            <Select
              labelId="charging-point-select-label"
              value={selectedChargingPoint}
              onChange={handleChargingPointChange}
              label="Select Device"
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: "#eaeff1",
                  },
                },
              }}
            >
              {chargingPoints.map((point) => (
                <MenuItem key={point.chargePointId} value={point.chargePointId}>
                  {point.chargePointId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {isLoading ? (
          <Box
            component="main"
            sx={{ flex: 1, py: 6, px: 4 }}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Guage
            selectedChargingPoint={selectedChargingPoint}
            selectedBuilding={selectedBuilding}
            onToggle={setSwitchState}
          />
        )}
        <Stats 
          selectedChargingPoint={selectedChargingPoint} 
          switchState={switchState}
        />
      </Box>
    </>
  );
}
